/**
 * Every operator route on the backend, once each — the ONLY file in web/ that
 * names a `/v1/admin` path.
 *
 * The backend relays the services' JSON as-is (spec 2026-08-29 §6), so the
 * types below are transcribed from the services' own response models
 * (`postman/imageshield-services-admin.postman_collection.json`, and
 * `image_flashbacklabs/src/imageshield/http/models.py`). Nothing validates them
 * at runtime — a drift shows up as visibly wrong data next to the raw body every
 * screen renders, rather than being coerced into looking right.
 *
 * NEVER an `operator` field in any body: the backend injects the grant's
 * display_name and its strict schemas reject a client-sent one.
 */
import { apiRequest } from '../api/client.js';
import type { ArticleBody, ThreatEventBody } from './forms.js';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Who am I
// ---------------------------------------------------------------------------

export interface OperatorMe {
  account_id: string;
  /** The exact string the backend forwards as `operator` on every write. */
  display_name: string;
}

/** 200 → an operator; 403 NOT_AN_OPERATOR → a signed-in account with no grant. */
export function getOperatorMe(): Promise<OperatorMe> {
  return apiRequest<OperatorMe>('GET', '/v1/admin/me');
}

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

export interface ProviderAlarm {
  kind: string;
  detail: string;
  provider_id?: string;
}

export interface ProviderHealth {
  provider_id: string;
  enabled: boolean;
  breaker_state: string;
  breaker_reason: string | null;
  call_count: number;
  /** Money crosses as decimal STRINGS. Never parsed here. */
  cost_usd: string;
  daily_budget_usd: string | null;
  monthly_budget_usd: string | null;
  month_to_date_cost_usd: string;
  budget_headroom_usd: string | null;
  /** null = no calls in the window — not 0. */
  success_rate: number | null;
  window_call_count: number;
  successful_calls_24h: number;
  latency_p50_ms: number | null;
  latency_p99_ms: number | null;
  alarms: ProviderAlarm[];
}

export interface ProviderHealthRead {
  as_of: string;
  window_hours: number;
  providers: ProviderHealth[];
}

export interface ProviderActionResult {
  provider_id: string;
  enabled: boolean | null;
  breaker_state: string | null;
}

export function getProviderHealth(): Promise<ProviderHealthRead> {
  return apiRequest<ProviderHealthRead>('GET', '/v1/admin/providers/health');
}

export function disableProvider(providerId: string, reason: string): Promise<ProviderActionResult> {
  return apiRequest<ProviderActionResult>('POST', `/v1/admin/providers/${enc(providerId)}/disable`, {
    body: { reason },
  });
}

export function enableProvider(providerId: string, reason: string): Promise<ProviderActionResult> {
  return apiRequest<ProviderActionResult>('POST', `/v1/admin/providers/${enc(providerId)}/enable`, {
    body: { reason },
  });
}

export function resetBreaker(providerId: string, reason: string): Promise<ProviderActionResult> {
  return apiRequest<ProviderActionResult>('POST', `/v1/admin/providers/${enc(providerId)}/breaker-reset`, {
    body: { reason },
  });
}

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------

export interface ReviewTask {
  task_id: string;
  infringement_id: string;
  user_ref: string;
  severity: string;
  /** Situational keys: best_face_bbox, face_match_score, moderation_labels, skipped, unfetchable… */
  triage: Record<string, unknown>;
  /** EVIDENCE for URL-context review. Rendered as text only — never a link, never an <img>. */
  image_url: string | null;
  page_url: string;
  face_match_score: number | null;
  source_domain: string;
}

export type ReviewQueue = Record<string, number>;

export interface SubjectDecision {
  occurred_at: string;
  user_ref: string;
  infringement_id: string;
  decision: string;
  severity: string | null;
  source_domain: string;
}

export interface OpenHit {
  user_ref: string;
  infringement_id: string;
  confirm_state: string;
  severity: string | null;
  source_domain: string;
  first_seen_at: string;
}

export interface DecisionResult {
  infringement_id: string;
  user_ref: string;
  decision: string;
  severity: string | null;
}

/** 204 upstream and here when the queue is empty — surfaced as null, a normal state. */
export async function getReviewNext(): Promise<ReviewTask | null> {
  const task = await apiRequest<ReviewTask | undefined>('GET', '/v1/admin/review/next');
  return task ?? null;
}

export function getReviewQueue(): Promise<ReviewQueue> {
  return apiRequest<ReviewQueue>('GET', '/v1/admin/review/queue');
}

export function getSubjectDecisions(limit: number): Promise<{ decisions: SubjectDecision[] }> {
  return apiRequest<{ decisions: SubjectDecision[] }>('GET', '/v1/admin/review/subject-decisions', {
    query: { limit },
  });
}

export function getOpenHits(limit: number): Promise<{ hits: OpenHit[] }> {
  return apiRequest<{ hits: OpenHit[] }>('GET', '/v1/admin/review/open-hits', { query: { limit } });
}

/** `severity` omitted keeps the machine-triage value; sent, it is an override. */
export function decideReview(
  taskId: string,
  decision: 'confirmed' | 'rejected' | 'uncertain',
  severity?: string,
): Promise<DecisionResult> {
  return apiRequest<DecisionResult>('POST', `/v1/admin/review/${enc(taskId)}/decision`, {
    body: severity === undefined ? { decision } : { decision, severity },
  });
}

// ---------------------------------------------------------------------------
// Threat events
// ---------------------------------------------------------------------------

export interface ThreatEvent {
  event_id: string;
  kind: string;
  title: string;
  body: string;
  severity: number;
  domains: string[];
  is_global: boolean;
  /** Decimal STRING. */
  penalty: string;
  starts_at: string;
  expires_at: string;
  decay_days: number;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ThreatEventCreated {
  event_id: string;
  matched_count: number;
}

export interface ThreatEventRetracted {
  event_id: string;
  matched_count: number;
  status: string;
}

export function listThreatEvents(): Promise<{ events: ThreatEvent[] }> {
  return apiRequest<{ events: ThreatEvent[] }>('GET', '/v1/admin/threat-events');
}

/**
 * Idempotency-Key REQUIRED by the backend (a create with a real external side
 * effect). One key per intent, reused across retries — a 409 with retry_after is
 * a duplicate in flight and is retried with the same key.
 */
export function createThreatEvent(body: ThreatEventBody, idempotencyKey: string): Promise<ThreatEventCreated> {
  return apiRequest<ThreatEventCreated>('POST', '/v1/admin/threat-events', {
    body,
    idempotencyKey,
    retryConflict: true,
  });
}

export function retractThreatEvent(eventId: string, reason: string): Promise<ThreatEventRetracted> {
  return apiRequest<ThreatEventRetracted>('POST', `/v1/admin/threat-events/${enc(eventId)}/retract`, {
    body: { reason },
  });
}

// ---------------------------------------------------------------------------
// Scores
// ---------------------------------------------------------------------------

export interface ScoreJournalEntry {
  score_event_id?: number;
  delta: number;
  component: string;
  cause_kind: string;
  /** An opaque provenance id, never a URL. */
  cause_ref: string | null;
  config_version?: string;
  score_after: number;
  created_at: string;
}

export interface AdminScoreRead {
  score: {
    score: number;
    components: Record<string, number>;
    config_version: string;
    computed_at: string;
  };
  /** Newest first. */
  events: ScoreJournalEntry[];
}

/** 404 SCORE_NOT_FOUND = nothing computed yet for that ref — a state, not an error. */
export function getAdminScore(userRef: string): Promise<AdminScoreRead> {
  return apiRequest<AdminScoreRead>('GET', `/v1/admin/scores/${enc(userRef)}`);
}

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

export type ArticleStatus = 'draft' | 'published' | 'archived';

export interface Article extends ArticleBody {
  article_id: string;
  status: ArticleStatus;
  published_at: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface ArticleLifecycle {
  article_id: string;
  status: ArticleStatus;
}

/** What the backend allows, mirrored so a hopeless upload never leaves the browser. */
const ARTICLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ARTICLE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

interface MintedArticleImage {
  upload_url: string;
  image_url: string;
  expires_at: string;
}

/**
 * Upload one article picture: presign here, then PUT the bytes STRAIGHT TO S3.
 *
 * The bytes never pass through the api (P17) — this is the same shape the app
 * uses for a photo. The backend signs the PUT for one content type and one
 * exact byte count, so both headers below must match what was asked for or S3
 * refuses the object; they are not decoration.
 *
 * Returns the stored, unsigned object URL — the only kind of picture URL an
 * article accepts. The app is served a short-lived signed GET for it at read
 * time, so this URL is not something to render directly.
 */
export async function uploadArticleImage(file: File): Promise<string> {
  if (!ARTICLE_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Pictures must be JPEG, PNG or WebP.');
  }
  if (file.size < 1 || file.size > ARTICLE_IMAGE_MAX_BYTES) {
    throw new Error('Pictures must be under 5 MB.');
  }
  const minted = await apiRequest<MintedArticleImage>('POST', '/v1/admin/articles/images', {
    body: { content_type: file.type, content_length: file.size },
  });
  const stored = await fetch(minted.upload_url, {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!stored.ok) {
    // S3 answers with an XML body no operator can read, so the status is the
    // only honest thing to show. A picture that failed to store must never
    // reach the form as if it had.
    throw new Error(`The picture could not be stored (${String(stored.status)}). Try again.`);
  }
  return minted.image_url;
}

export function listArticles(limit: number): Promise<{ articles: Article[] }> {
  return apiRequest<{ articles: Article[] }>('GET', '/v1/admin/articles', { query: { limit } });
}

/** Idempotency-Key REQUIRED — see createThreatEvent. */
export function createArticle(body: ArticleBody, idempotencyKey: string): Promise<ArticleLifecycle> {
  return apiRequest<ArticleLifecycle>('POST', '/v1/admin/articles', {
    body,
    idempotencyKey,
    retryConflict: true,
  });
}

export function getArticle(articleId: string): Promise<Article> {
  return apiRequest<Article>('GET', `/v1/admin/articles/${enc(articleId)}`);
}

export function updateArticle(articleId: string, body: ArticleBody): Promise<Article> {
  return apiRequest<Article>('PUT', `/v1/admin/articles/${enc(articleId)}`, { body });
}

/** No body — the route takes none. */
export function publishArticle(articleId: string): Promise<ArticleLifecycle> {
  return apiRequest<ArticleLifecycle>('POST', `/v1/admin/articles/${enc(articleId)}/publish`);
}

export function archiveArticle(articleId: string, reason: string): Promise<ArticleLifecycle> {
  return apiRequest<ArticleLifecycle>('POST', `/v1/admin/articles/${enc(articleId)}/archive`, {
    body: { reason },
  });
}
